import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Exhibit } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, GripVertical, Eye, EyeOff } from "lucide-react";
import { colors, fonts, shadows } from "@/lib/theme";

interface ExhibitListItemProps {
  exhibit: Exhibit;
  onEdit: () => void;
  onDelete: () => void;
  onToggleVisibility: () => void;
}

/**
 * Sortable list item for exhibit management
 * Shows thumbnails, title, image count, and action buttons
 */
export function ExhibitListItem({
  exhibit,
  onEdit,
  onDelete,
  onToggleVisibility,
}: ExhibitListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: exhibit.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const imageCount = exhibit.images.length;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: exhibit.visible ? colors.surface : "#F5F5F5",
        borderRadius: "8px",
        boxShadow: shadows.card,
        opacity: exhibit.visible ? 1 : 0.7,
      }}
      className="p-3 md:p-4 mb-3"
    >
      <div className="flex items-center gap-3 md:gap-4">
        {/* Drag handle - larger touch target on mobile */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -m-1"
        >
          <GripVertical size={24} style={{ color: colors.textMuted }} />
        </div>

        {/* Image thumbnails - hidden on mobile */}
        <div className="hidden md:block">
          <ImageThumbnails images={exhibit.images} />
        </div>

        {/* Title and metadata - clickable for Gallery to enable quick upload */}
        <div
          className={`flex-1 min-w-0 ${exhibit.isGallery ? "cursor-pointer" : ""}`}
          onClick={exhibit.isGallery ? onEdit : undefined}
        >
          <h3
            className="truncate"
            style={{ fontFamily: fonts.serif, color: colors.text }}
          >
            {exhibit.isGallery ? (
              <span style={{ color: colors.primary }}>Gallery</span>
            ) : exhibit.title ? (
              exhibit.title
            ) : (
              <span style={{ color: colors.textMuted, fontStyle: "italic" }}>Untitled</span>
            )}
          </h3>
          <div className="flex flex-wrap gap-2 mt-1">
            <span
              className="inline-block px-2 py-0.5 rounded text-xs"
              style={{ backgroundColor: colors.border, color: colors.textMuted }}
            >
              {imageCount} image{imageCount !== 1 ? "s" : ""}
            </span>
            {exhibit.isGallery && (
              <span
                className="inline-block px-2 py-0.5 rounded text-xs"
                style={{ backgroundColor: colors.primaryLight, color: colors.primary }}
              >
                Tap to upload
              </span>
            )}
          </div>
        </div>

        {/* Action buttons - larger on mobile */}
        <div className="flex gap-1 md:gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={onToggleVisibility}
            title={exhibit.visible ? "Hide from gallery" : "Show on gallery"}
            className="h-10 w-10 md:h-9 md:w-9"
          >
            {exhibit.visible ? <Eye size={18} /> : <EyeOff size={18} />}
          </Button>
          {!exhibit.isGallery && (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={onEdit}
                className="h-10 w-10 md:h-9 md:w-9"
              >
                <Edit size={18} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={onDelete}
                className="h-10 w-10 md:h-9 md:w-9"
                style={{ borderColor: colors.accent, color: colors.accent }}
              >
                <Trash2 size={18} />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Displays up to 3 thumbnails with overflow indicator
 */
function ImageThumbnails({ images }: { images: Exhibit["images"] }) {
  const visibleImages = images.slice(0, 3);
  const remainingCount = images.length - 3;

  return (
    <div className="flex gap-1">
      {visibleImages.map((img) => (
        <img
          key={img.id}
          src={img.thumbnailPath}
          alt=""
          className="w-12 h-12 object-cover rounded"
          style={{ backgroundColor: colors.border }}
          onError={(e) => {
            e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E";
          }}
        />
      ))}
      {remainingCount > 0 && (
        <div
          className="w-12 h-12 rounded flex items-center justify-center text-sm"
          style={{ backgroundColor: colors.border, color: colors.textMuted }}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
}
